import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { 
  FlaskConical, TrendingUp, Users, CheckCircle, Clock, 
  AlertTriangle, ArrowRight, Bell, Rocket, BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Experiment {
  id: string;
  name: string;
  description: string;
  intervention_type: string;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
  treatment_group_size: number;
  control_group_size: number;
  effect_size: number | null;
  p_value: number | null;
  conclusion: string | null;
  variant_a: any;
  variant_b: any;
  outcome_metrics: any;
}

interface ExperimentNotification {
  id: string;
  experiment_id: string;
  notification_type: string;
  title: string;
  summary: string;
  recommended_interventions: any;
  is_read: boolean;
  created_at: string;
}

interface AdminExperimentResultsProps {
  siteId?: string | null;
}

// Note: Experiments are company-wide, not site-specific. The siteId prop is accepted for interface consistency
// but experiments are not filtered by site as they apply across the organization.
const AdminExperimentResults = ({ siteId: _propSiteId }: AdminExperimentResultsProps = {}) => {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [notifications, setNotifications] = useState<ExperimentNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [showImplementDialog, setShowImplementDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch experiments (company-wide, not site-filtered)
      const { data: expData, error: expError } = await supabase
        .from('intervention_experiments')
        .select('*')
        .order('start_date', { ascending: false });

      if (expError) throw expError;
      setExperiments(expData || []);

      // Fetch notifications
      const { data: notifData, error: notifError } = await supabase
        .from('experiment_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (!notifError) {
        setNotifications(notifData || []);
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markNotificationRead = async (id: string) => {
    const { error } = await supabase
      .from('experiment_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    }
  };

  const handleImplementIntervention = async () => {
    if (!selectedExperiment) return;

    try {
      // Create intervention based on winning variant
      const winningVariant = (selectedExperiment.effect_size || 0) > 0 
        ? selectedExperiment.variant_a 
        : selectedExperiment.variant_b;

      await supabase.from('interventions').insert({
        title: `${selectedExperiment.name} - Rollout`,
        message: winningVariant?.message || 'Based on experiment results',
        category: 'behavioral',
        delivery_method: winningVariant?.delivery_method || 'notification',
        target_type: 'all',
        trigger_type: 'experiment_rollout',
        status: 'pending',
      });

      toast({
        title: 'Intervention Created',
        description: 'The winning intervention has been scheduled for rollout.',
      });

      setShowImplementDialog(false);
    } catch (error) {
      console.error('Error implementing intervention:', error);
      toast({
        title: 'Error',
        description: 'Failed to create intervention',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (exp: Experiment) => {
    if (exp.is_active) {
      return <Badge className="bg-primary">Running</Badge>;
    }
    if (exp.conclusion) {
      return <Badge className="bg-success text-success-foreground">Completed</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  const getSignificanceBadge = (pValue: number | null) => {
    if (pValue === null) return null;
    if (pValue < 0.01) return <Badge className="bg-success text-success-foreground">Highly Significant (p&lt;0.01)</Badge>;
    if (pValue < 0.05) return <Badge className="bg-success text-success-foreground">Significant (p&lt;0.05)</Badge>;
    return <Badge variant="secondary">Not Significant</Badge>;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Experiment Results</h2>
          <p className="text-muted-foreground">View behavioral experiment outcomes and implement winning interventions</p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="destructive" className="animate-pulse">
            {unreadCount} new results
          </Badge>
        )}
      </div>

      {/* New Results Notifications */}
      {notifications.filter(n => !n.is_read).length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              New Experiment Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notifications.filter(n => !n.is_read).map(notif => (
                <div 
                  key={notif.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background border cursor-pointer hover:bg-muted/50"
                  onClick={() => {
                    markNotificationRead(notif.id);
                    const exp = experiments.find(e => e.id === notif.experiment_id);
                    if (exp) setSelectedExperiment(exp);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <FlaskConical className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{notif.title}</p>
                      <p className="text-sm text-muted-foreground">{notif.summary}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="completed">
        <TabsList>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="running">Running</TabsTrigger>
          <TabsTrigger value="all">All Experiments</TabsTrigger>
        </TabsList>

        <TabsContent value="completed" className="mt-4">
          <div className="grid gap-4">
            {experiments.filter(e => !e.is_active && e.conclusion).map(exp => (
              <ExperimentCard 
                key={exp.id} 
                experiment={exp} 
                onSelect={() => setSelectedExperiment(exp)}
                onImplement={() => {
                  setSelectedExperiment(exp);
                  setShowImplementDialog(true);
                }}
              />
            ))}
            {experiments.filter(e => !e.is_active && e.conclusion).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No completed experiments yet
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="running" className="mt-4">
          <div className="grid gap-4">
            {experiments.filter(e => e.is_active).map(exp => (
              <ExperimentCard 
                key={exp.id} 
                experiment={exp} 
                onSelect={() => setSelectedExperiment(exp)}
              />
            ))}
            {experiments.filter(e => e.is_active).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No experiments currently running
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <div className="grid gap-4">
            {experiments.map(exp => (
              <ExperimentCard 
                key={exp.id} 
                experiment={exp} 
                onSelect={() => setSelectedExperiment(exp)}
                onImplement={exp.conclusion ? () => {
                  setSelectedExperiment(exp);
                  setShowImplementDialog(true);
                } : undefined}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Experiment Detail Dialog */}
      <Dialog open={!!selectedExperiment && !showImplementDialog} onOpenChange={(open) => !open && setSelectedExperiment(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedExperiment?.name}</DialogTitle>
          </DialogHeader>
          {selectedExperiment && (
            <div className="space-y-4">
              <p className="text-muted-foreground">{selectedExperiment.description}</p>
              
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Treatment Group</p>
                    <p className="text-2xl font-bold">{selectedExperiment.treatment_group_size}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Control Group</p>
                    <p className="text-2xl font-bold">{selectedExperiment.control_group_size}</p>
                  </CardContent>
                </Card>
              </div>

              {selectedExperiment.effect_size !== null && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">Effect Size</p>
                      {getSignificanceBadge(selectedExperiment.p_value)}
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`h-5 w-5 ${(selectedExperiment.effect_size || 0) > 0 ? 'text-success' : 'text-destructive'}`} />
                      <span className="text-2xl font-bold">
                        {selectedExperiment.effect_size > 0 ? '+' : ''}{(selectedExperiment.effect_size * 100).toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedExperiment.conclusion && (
                <Card className="border-success/50 bg-success/5">
                  <CardContent className="p-4">
                    <p className="font-medium mb-2">Conclusion</p>
                    <p>{selectedExperiment.conclusion}</p>
                  </CardContent>
                </Card>
              )}

              {selectedExperiment.conclusion && (
                <Button 
                  onClick={() => setShowImplementDialog(true)} 
                  className="w-full"
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Implement Winning Intervention
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Implement Dialog */}
      <Dialog open={showImplementDialog} onOpenChange={setShowImplementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Implement Intervention</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Based on the experiment results, we recommend rolling out the winning intervention to all workers.
            </p>
            {selectedExperiment && (
              <Card>
                <CardContent className="p-4">
                  <p className="font-medium">{selectedExperiment.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Effect size: {selectedExperiment.effect_size ? `${(selectedExperiment.effect_size * 100).toFixed(1)}%` : 'N/A'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImplementDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImplementIntervention}>
              <Rocket className="h-4 w-4 mr-2" />
              Implement Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Experiment Card Component
const ExperimentCard = ({ 
  experiment, 
  onSelect,
  onImplement 
}: { 
  experiment: Experiment; 
  onSelect: () => void;
  onImplement?: () => void;
}) => {
  const getStatusBadge = () => {
    if (experiment.is_active) return <Badge className="bg-primary">Running</Badge>;
    if (experiment.conclusion) return <Badge className="bg-success text-success-foreground">Completed</Badge>;
    return <Badge variant="secondary">Pending</Badge>;
  };

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onSelect}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical className="h-4 w-4 text-primary" />
              <h3 className="font-semibold">{experiment.name}</h3>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-muted-foreground mb-2">{experiment.description}</p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {experiment.treatment_group_size + experiment.control_group_size} participants
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(new Date(experiment.start_date), 'dd MMM yyyy')}
              </span>
            </div>
          </div>
          {experiment.effect_size !== null && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Effect</p>
              <p className={`text-lg font-bold ${experiment.effect_size > 0 ? 'text-success' : 'text-destructive'}`}>
                {experiment.effect_size > 0 ? '+' : ''}{(experiment.effect_size * 100).toFixed(0)}%
              </p>
            </div>
          )}
        </div>
        {onImplement && (
          <div className="mt-3 pt-3 border-t">
            <Button 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onImplement(); }}
              className="w-full"
            >
              <Rocket className="h-4 w-4 mr-2" />
              Implement
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminExperimentResults;
